// Vendor Onboarding — public 9-step wizard.
// URL: /vendor/signup/:slug   or   /vendor/resume/:token
//
// No auth required. The progress_token is the secret that authorises every
// write — it's emailed to the vendor and lives in localStorage as a fallback.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, ShieldCheck, MailCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/vendor-onboarding`;

const STEPS = [
  { key: "basics",     label: "Basic info" },
  { key: "tax",        label: "Tax & registration" },
  { key: "contacts",   label: "Contacts" },
  { key: "addresses",  label: "Addresses" },
  { key: "banking",    label: "Banking" },
  { key: "capabilities", label: "Capabilities" },
  { key: "compliance", label: "Compliance" },
  { key: "documents",  label: "Documents" },
  { key: "review",     label: "Legal & submit" },
];

const ENTITY_TYPES = [
  ["proprietorship", "Proprietorship"],
  ["partnership",    "Partnership"],
  ["llp",            "LLP"],
  ["pvt_ltd",        "Private Limited"],
  ["public_ltd",     "Public Limited"],
  ["huf",            "HUF"],
  ["trust",          "Trust"],
  ["society",        "Society"],
  ["foreign",        "Foreign entity"],
  ["other",          "Other"],
];

const HEAD_RANGES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const TURNOVER = ["<1Cr", "1-10Cr", "10-100Cr", "100Cr+"];
const PAYMENT_TERMS = ["advance", "NET15", "NET30", "NET45", "NET60"];

const BIZ_CATEGORIES = [
  "IT hardware", "Software & licences", "Office supplies", "Logistics",
  "Marketing & PR", "Legal & professional", "Facilities & housekeeping",
  "Travel & hospitality", "Recruitment", "Training & L&D", "Manufacturing",
  "Raw materials", "Packaging", "Security services", "Catering", "Construction",
];

export default function VendorOnboardingPublic() {
  const { slug, token: urlToken } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState("loading");   // loading | start | otp | form | submitted | invalid
  const [tenant, setTenant] = useState(null);
  const [token, setToken] = useState(urlToken || localStorage.getItem("vendor_progress_token") || "");
  const [otp, setOtp] = useState("");
  const [startForm, setStartForm] = useState({ legal_name: "", primary_contact_email: "" });
  const [app, setApp] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  // ── Bootstrap ───────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        if (urlToken) {
          // Resume flow
          setToken(urlToken);
          localStorage.setItem("vendor_progress_token", urlToken);
          const r = await axios.get(`${API}/${urlToken}`);
          setApp(r.data.application);
          setTenant({ name: r.data.company.name, logo_url: r.data.company.logo_url, brand_color: r.data.company.brand_color });
          setStepIdx(Math.max(0, (r.data.application.current_step || 1) - 1));
          if (r.data.application.email_verified_at) setPhase("form");
          else setPhase("otp");
          return;
        }
        if (!slug) { setPhase("invalid"); return; }
        const r = await axios.get(`${API}/landing/${slug}`);
        setTenant(r.data);
        // Try to resume if we have a token
        if (token) {
          try {
            const d = await axios.get(`${API}/${token}`);
            if (d.data.application.company_id === r.data.company_id) {
              setApp(d.data.application);
              setStepIdx(Math.max(0, (d.data.application.current_step || 1) - 1));
              setPhase(d.data.application.email_verified_at ? "form" : "otp");
              return;
            }
          } catch (e) { /* token expired — fall through to start */ }
        }
        setPhase("start");
      } catch (e) {
        setPhase("invalid");
      }
    })();
    // eslint-disable-next-line
  }, [slug, urlToken]);

  // ── Step 0: Start ───────────────────────────────────────────────
  const startApplication = async () => {
    if (!startForm.primary_contact_email.trim()) return toast.error("Email is required");
    if (!startForm.legal_name.trim()) return toast.error("Company legal name is required");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/start`, { slug, ...startForm });
      setToken(r.data.progress_token);
      localStorage.setItem("vendor_progress_token", r.data.progress_token);
      toast.success(r.data.resumed ? "Welcome back — verification code re-sent" : "Verification code sent to your email");
      setPhase("otp");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start application"); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length < 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await axios.post(`${API}/${token}/verify-otp`, { otp });
      toast.success("Email verified — let's get started");
      // Now load the draft
      const d = await axios.get(`${API}/${token}`);
      setApp(d.data.application);
      setStepIdx(0);
      setPhase("form");
    } catch (e) { toast.error(e.response?.data?.detail || "Wrong code"); }
    finally { setBusy(false); }
  };

  const resendOtp = async () => {
    try { await axios.post(`${API}/${token}/resend-otp`); toast.success("Code re-sent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't resend"); }
  };

  // ── Form helpers ────────────────────────────────────────────────
  const setField = (k, v) => setApp(a => ({ ...a, [k]: v }));
  const setNested = (parent, k, v) => setApp(a => ({ ...a, [parent]: { ...(a?.[parent] || {}), [k]: v } }));

  const saveDraft = async (patch = null) => {
    setSaving(true);
    try {
      const body = patch || pickWritable(app, stepIdx);
      await axios.patch(`${API}/${token}`, { ...body, current_step: stepIdx + 1 });
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const next = async () => {
    const err = validateStep(app, stepIdx);
    if (err) return toast.error(err);
    await saveDraft();
    setStepIdx(i => Math.min(i + 1, STEPS.length - 1));
  };

  const prev = () => setStepIdx(i => Math.max(0, i - 1));

  // Auto-fills
  const lookupIFSC = async () => {
    if (!app.ifsc) return;
    try {
      const r = await axios.get(`${API}/lookup/ifsc/${app.ifsc}`);
      setApp(a => ({ ...a, bank_name: r.data.BANK || a.bank_name, bank_branch: r.data.BRANCH || a.bank_branch }));
      toast.success(`${r.data.BANK} • ${r.data.BRANCH}`);
    } catch (e) { toast.error("IFSC not found"); }
  };
  const lookupGSTIN = async () => {
    if (!app.gstin) return;
    try {
      const r = await axios.get(`${API}/lookup/gstin/${app.gstin}`);
      setApp(a => ({ ...a, gst_state_code: r.data.state_code, pan: a.pan || r.data.pan_in_gstin }));
      toast.success("GSTIN format validated");
    } catch (e) { toast.error("Invalid GSTIN"); }
  };

  // ── Submit ─────────────────────────────────────────────────────
  const submit = async () => {
    if (!app.msa_accepted || !app.code_of_conduct_accepted || !app.dpdp_consent) {
      return toast.error("Please accept all three consents");
    }
    setBusy(true);
    try {
      await saveDraft();
      await axios.post(`${API}/${token}/submit`, {
        msa_accepted: true, code_of_conduct_accepted: true, dpdp_consent: true,
      });
      setPhase("submitted");
      localStorage.removeItem("vendor_progress_token");
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  // ── Render ─────────────────────────────────────────────────────
  const brandColor = tenant?.brand_color || "#0f172a";

  if (phase === "loading") return <Shell><Center>Loading…</Center></Shell>;
  if (phase === "invalid") return <Shell><Center><p className="text-zinc-700">This onboarding link is invalid or has been rotated. Ask the procurement team for a fresh one.</p></Center></Shell>;

  if (phase === "submitted") return (
    <Shell tenant={tenant}>
      <div className="bg-white border border-emerald-200 rounded-2xl p-10 max-w-xl mx-auto text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
          <Check className="text-emerald-600" size={32}/>
        </div>
        <h2 className="text-2xl font-semibold text-zinc-900 mb-2">Application submitted</h2>
        <p className="text-zinc-600 mb-6">{tenant?.company_name || "The team"} will review your details and email you with a decision within 3–5 working days.</p>
        <div className="bg-zinc-50 rounded-lg p-3 text-xs font-mono text-zinc-700" data-testid="submitted-code">
          Reference: {app?.application_code}
        </div>
        <Button onClick={() => navigate("/")} variant="outline" className="mt-6">Done</Button>
      </div>
    </Shell>
  );

  if (phase === "start") return (
    <Shell tenant={tenant}>
      <div className="bg-white border border-zinc-200 rounded-2xl p-8 max-w-xl mx-auto" data-testid="start-card">
        <h2 className="text-2xl font-semibold text-zinc-900 mb-1">Become a vendor with {tenant?.company_name}</h2>
        <p className="text-sm text-zinc-600 mb-6">Tell us your work email — we'll send a 6-digit code to verify it before you fill the form.</p>
        <div className="space-y-3">
          <div><Label>Company legal name *</Label><Input value={startForm.legal_name} onChange={e=>setStartForm(s=>({...s, legal_name: e.target.value}))} placeholder="ACME Industries Pvt Ltd" data-testid="start-legal"/></div>
          <div><Label>Your work email *</Label><Input type="email" value={startForm.primary_contact_email} onChange={e=>setStartForm(s=>({...s, primary_contact_email: e.target.value}))} placeholder="finance@acme.in" data-testid="start-email"/></div>
        </div>
        <Button onClick={startApplication} disabled={busy} className="w-full mt-6" style={{ background: brandColor }} data-testid="start-btn">
          {busy ? "Sending code…" : "Get verification code"} <ArrowRight size={16} className="ml-1.5"/>
        </Button>
        <p className="text-xs text-zinc-500 mt-4">By starting an application you consent to {tenant?.company_name} processing your business data for procurement evaluation.</p>
      </div>
    </Shell>
  );

  if (phase === "otp") return (
    <Shell tenant={tenant}>
      <div className="bg-white border border-zinc-200 rounded-2xl p-8 max-w-md mx-auto text-center" data-testid="otp-card">
        <MailCheck size={36} className="mx-auto text-zinc-700 mb-3"/>
        <h2 className="text-xl font-semibold text-zinc-900 mb-1">Check your email</h2>
        <p className="text-sm text-zinc-600 mb-5">We sent a 6-digit code. It's valid for 15 minutes.</p>
        <Input value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,"").slice(0,6))} maxLength={6}
               className="text-center text-2xl tracking-[0.5em] font-mono" placeholder="000000" data-testid="otp-input"/>
        <Button onClick={verifyOtp} disabled={busy || otp.length < 6} className="w-full mt-4" style={{ background: brandColor }} data-testid="otp-verify">
          Verify & continue
        </Button>
        <button onClick={resendOtp} className="text-xs text-zinc-500 hover:text-zinc-800 mt-3" data-testid="otp-resend">Resend code</button>
      </div>
    </Shell>
  );

  // FORM
  return (
    <Shell tenant={tenant} subnav={
      <div className="flex items-center gap-1 overflow-x-auto py-2">
        {STEPS.map((s, i) => (
          <button key={s.key} onClick={() => setStepIdx(i)}
                  data-testid={`step-${s.key}`}
                  className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap ${i === stepIdx ? "bg-zinc-900 text-white border-zinc-900" : i < stepIdx ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-white text-zinc-600 border-zinc-200"}`}>
            {i + 1}. {s.label}
          </button>
        ))}
      </div>
    }>
      <div className="bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8 max-w-4xl mx-auto">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Step {stepIdx + 1} of {STEPS.length}</p>
            <h2 className="text-2xl font-semibold text-zinc-900">{STEPS[stepIdx].label}</h2>
          </div>
          {saving && <span className="text-xs text-zinc-500">Saving…</span>}
          {!saving && <Badge className="bg-emerald-100 text-emerald-700">Draft saved</Badge>}
        </div>

        <StepView idx={stepIdx} app={app} setField={setField} setNested={setNested}
                  lookupIFSC={lookupIFSC} lookupGSTIN={lookupGSTIN} tenant={tenant}/>

        <div className="flex items-center justify-between mt-8 pt-5 border-t">
          <Button variant="outline" onClick={prev} disabled={stepIdx === 0}>
            <ArrowLeft size={16} className="mr-1.5"/> Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => saveDraft()} data-testid="save-draft">Save draft</Button>
            {stepIdx < STEPS.length - 1 ? (
              <Button onClick={next} style={{ background: brandColor }} data-testid="next-step">
                Save & continue <ArrowRight size={16} className="ml-1.5"/>
              </Button>
            ) : (
              <Button onClick={submit} disabled={busy} style={{ background: brandColor }} data-testid="submit-app">
                {busy ? "Submitting…" : "Submit application"} <ShieldCheck size={16} className="ml-1.5"/>
              </Button>
            )}
          </div>
        </div>
      </div>
      <p className="text-center text-xs text-zinc-500 mt-6">Your progress is saved automatically. Bookmark this page to come back later.</p>
    </Shell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Step content
// ────────────────────────────────────────────────────────────────────

function StepView({ idx, app, setField, setNested, lookupIFSC, lookupGSTIN, tenant }) {
  const a = app || {};
  if (idx === 0) return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Legal name *"><Input value={a.legal_name||""} onChange={e=>setField("legal_name", e.target.value)} data-testid="legal_name"/></Field>
      <Field label="Trade / brand name"><Input value={a.trade_name||""} onChange={e=>setField("trade_name", e.target.value)} placeholder="Public-facing brand if different"/></Field>
      <Field label="Entity type *">
        <Select value={a.entity_type||""} onValueChange={v=>setField("entity_type", v)}>
          <SelectTrigger data-testid="entity_type"><SelectValue placeholder="Select"/></SelectTrigger>
          <SelectContent>{ENTITY_TYPES.map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Year established"><Input type="number" value={a.year_established||""} onChange={e=>setField("year_established", Number(e.target.value)||"")} placeholder="2015"/></Field>
      <Field label="Website"><Input value={a.website||""} onChange={e=>setField("website", e.target.value)} placeholder="https://"/></Field>
      <Field label="Employee count">
        <Select value={a.employee_count_range||""} onValueChange={v=>setField("employee_count_range", v)}>
          <SelectTrigger><SelectValue placeholder="Range"/></SelectTrigger>
          <SelectContent>{HEAD_RANGES.map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
    </div>
  );

  if (idx === 1) return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="PAN *">
        <Input value={a.pan||""} onChange={e=>setField("pan", e.target.value.toUpperCase().slice(0,10))} placeholder="ABCDE1234F" data-testid="pan"/>
      </Field>
      <Field label="GSTIN (with format check)">
        <div className="flex gap-2">
          <Input value={a.gstin||""} onChange={e=>setField("gstin", e.target.value.toUpperCase().slice(0,15))} placeholder="29ABCDE1234F1Z5" data-testid="gstin"/>
          <Button variant="outline" onClick={lookupGSTIN}>Validate</Button>
        </div>
      </Field>
      <Field label="TAN"><Input value={a.tan||""} onChange={e=>setField("tan", e.target.value.toUpperCase())}/></Field>
      <Field label="CIN"><Input value={a.cin||""} onChange={e=>setField("cin", e.target.value.toUpperCase())} placeholder="U72200KA2015PTC123456"/></Field>
      <Field label="Udyam (MSME) number"><Input value={a.udyam_number||""} onChange={e=>setField("udyam_number", e.target.value.toUpperCase())} placeholder="UDYAM-KR-29-1234567"/></Field>
      <Field label="MSME category">
        <Select value={a.msme_category||""} onValueChange={v=>setField("msme_category", v)}>
          <SelectTrigger><SelectValue placeholder="(if applicable)"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="micro">Micro</SelectItem><SelectItem value="small">Small</SelectItem><SelectItem value="medium">Medium</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="IEC code (importer/exporter)"><Input value={a.iec_code||""} onChange={e=>setField("iec_code", e.target.value.toUpperCase())}/></Field>
      <Field label="FSSAI (F&B only)"><Input value={a.fssai||""} onChange={e=>setField("fssai", e.target.value)}/></Field>
    </div>
  );

  if (idx === 2) return (
    <div className="space-y-6">
      <Section title="Primary contact">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name *"><Input value={a.primary_contact_name||""} onChange={e=>setField("primary_contact_name", e.target.value)} data-testid="primary_contact_name"/></Field>
          <Field label="Designation"><Input value={a.primary_contact_designation||""} onChange={e=>setField("primary_contact_designation", e.target.value)}/></Field>
          <Field label="Email (verified)"><Input value={a.primary_contact_email||""} disabled className="bg-zinc-50"/></Field>
          <Field label="Phone *"><Input value={a.primary_contact_phone||""} onChange={e=>setField("primary_contact_phone", e.target.value)} placeholder="+91-9876543210" data-testid="primary_contact_phone"/></Field>
        </div>
      </Section>
      <Section title="Accounts / billing contact">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name"><Input value={a.accounts_contact_name||""} onChange={e=>setField("accounts_contact_name", e.target.value)}/></Field>
          <Field label="Email"><Input value={a.accounts_contact_email||""} onChange={e=>setField("accounts_contact_email", e.target.value)}/></Field>
          <Field label="Phone"><Input value={a.accounts_contact_phone||""} onChange={e=>setField("accounts_contact_phone", e.target.value)}/></Field>
        </div>
      </Section>
      <Section title="Escalation contact">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name"><Input value={a.escalation_contact_name||""} onChange={e=>setField("escalation_contact_name", e.target.value)}/></Field>
          <Field label="Email"><Input value={a.escalation_contact_email||""} onChange={e=>setField("escalation_contact_email", e.target.value)}/></Field>
          <Field label="Phone"><Input value={a.escalation_contact_phone||""} onChange={e=>setField("escalation_contact_phone", e.target.value)}/></Field>
        </div>
      </Section>
    </div>
  );

  if (idx === 3) return (
    <div className="space-y-6">
      <AddressBlock title="Registered address *" v={a.registered_address||{}} onChange={(k,v)=>setNested("registered_address", k, v)} testidPrefix="reg"/>
      <AddressBlock title="Operations address" v={a.operations_address||{}} onChange={(k,v)=>setNested("operations_address", k, v)} testidPrefix="ops"/>
    </div>
  );

  if (idx === 4) return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Account holder name *"><Input value={a.account_holder_name||""} onChange={e=>setField("account_holder_name", e.target.value)} data-testid="account_holder_name"/></Field>
        <Field label="Account number *"><Input value={a.account_number||""} onChange={e=>setField("account_number", e.target.value)} data-testid="account_number"/></Field>
        <Field label="IFSC *">
          <div className="flex gap-2">
            <Input value={a.ifsc||""} onChange={e=>setField("ifsc", e.target.value.toUpperCase().slice(0,11))} placeholder="HDFC0000001" data-testid="ifsc"/>
            <Button variant="outline" onClick={lookupIFSC}>Lookup</Button>
          </div>
        </Field>
        <Field label="Account type">
          <Select value={a.account_type||""} onValueChange={v=>setField("account_type", v)}>
            <SelectTrigger><SelectValue placeholder="Type"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="savings">Savings</SelectItem><SelectItem value="current">Current</SelectItem>
              <SelectItem value="cc">Cash Credit</SelectItem><SelectItem value="od">Overdraft</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Bank name *"><Input value={a.bank_name||""} onChange={e=>setField("bank_name", e.target.value)} data-testid="bank_name"/></Field>
        <Field label="Branch"><Input value={a.bank_branch||""} onChange={e=>setField("bank_branch", e.target.value)}/></Field>
        <Field label="UPI ID"><Input value={a.upi_id||""} onChange={e=>setField("upi_id", e.target.value)} placeholder="acme@hdfcbank"/></Field>
      </div>
      <p className="text-xs text-zinc-500">A cancelled cheque or bank letter should be uploaded in the Documents step to verify these details.</p>
    </div>
  );

  if (idx === 5) return (
    <div className="space-y-5">
      <Field label="Business categories you serve *">
        <ChipMulti options={BIZ_CATEGORIES} value={a.business_categories||[]} onChange={v=>setField("business_categories", v)}/>
      </Field>
      <Field label="Products & services offered *"><Textarea rows={4} value={a.products_services_offered||""} onChange={e=>setField("products_services_offered", e.target.value)} placeholder="One paragraph describing what you supply, your differentiators, key brands you work with…" data-testid="products"/></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Annual turnover">
          <Select value={a.annual_turnover_range||""} onValueChange={v=>setField("annual_turnover_range", v)}>
            <SelectTrigger><SelectValue placeholder="Range"/></SelectTrigger>
            <SelectContent>{TURNOVER.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Preferred payment terms">
          <Select value={a.payment_terms_requested||""} onValueChange={v=>setField("payment_terms_requested", v)}>
            <SelectTrigger><SelectValue placeholder="Terms"/></SelectTrigger>
            <SelectContent>{PAYMENT_TERMS.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Top 3 customer references">
        <Input placeholder="Customer 1" value={(a.key_customers||[])[0]||""} onChange={e=>updateArrAt(setField, app, "key_customers", 0, e.target.value)}/>
        <Input placeholder="Customer 2" className="mt-2" value={(a.key_customers||[])[1]||""} onChange={e=>updateArrAt(setField, app, "key_customers", 1, e.target.value)}/>
        <Input placeholder="Customer 3" className="mt-2" value={(a.key_customers||[])[2]||""} onChange={e=>updateArrAt(setField, app, "key_customers", 2, e.target.value)}/>
      </Field>
      <Field label="Regions / locations served">
        <Input placeholder="e.g. India · Pan-India / Karnataka, Maharashtra · APAC" value={(a.locations_served||[]).join(", ")} onChange={e=>setField("locations_served", e.target.value.split(",").map(s=>s.trim()).filter(Boolean))}/>
      </Field>
    </div>
  );

  if (idx === 6) return (
    <div className="space-y-5">
      <Field label="ISO certifications">
        <ChipMulti options={["ISO 9001","ISO 14001","ISO 27001","ISO 45001","ISO 22000"]} value={a.iso_certifications||[]} onChange={v=>setField("iso_certifications", v)}/>
      </Field>
      <Field label="Diversity / inclusion certifications">
        <ChipMulti options={["Women-owned","MSME","SC/ST owned","LGBTQ+ owned","Veteran-owned"]} value={a.diversity_certifications||[]} onChange={v=>setField("diversity_certifications", v)}/>
      </Field>
      <Field label="Active insurance policies">
        <ChipMulti options={["Public liability","Product liability","Professional indemnity","Cyber liability","Workmen's comp"]} value={a.insurance_policies||[]} onChange={v=>setField("insurance_policies", v)}/>
      </Field>
      <div className="border rounded-lg p-4 bg-amber-50/40 space-y-3">
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={!!a.has_been_blacklisted} onChange={e=>setField("has_been_blacklisted", e.target.checked)} data-testid="blacklisted"/>
          <span>Has the company ever been blacklisted, suspended or debarred by any customer or government body?</span>
        </label>
        {a.has_been_blacklisted && (
          <Textarea rows={3} placeholder="Disclose the customer, year and reason" value={a.blacklist_disclosure||""} onChange={e=>setField("blacklist_disclosure", e.target.value)}/>
        )}
        <Field label="Ongoing litigation (if any)">
          <Textarea rows={2} placeholder="Court / forum, opposing party, brief description" value={a.litigation_disclosure||""} onChange={e=>setField("litigation_disclosure", e.target.value)}/>
        </Field>
      </div>
    </div>
  );

  if (idx === 7) return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-600">Document uploads (PAN, GST cert, cancelled cheque, MSME cert, ISO certs, etc.) will be enabled in the next iteration. For now, mention which documents you can provide and we'll reach out to collect them.</p>
      <Field label="Documents you can provide on request">
        <ChipMulti options={[
          "PAN card","GST certificate","MSME / Udyam certificate","Cancelled cheque",
          "Incorporation certificate","ISO certificates","Insurance policies","Annual report",
          "Company brochure","ESIC / PF certificate","Bank statement (6 months)",
        ]} value={(a.documents||[]).map(d=>d.label).filter(Boolean)} onChange={v=>setField("documents", v.map(label=>({id:label, kind:"other", label})))}/>
      </Field>
    </div>
  );

  if (idx === 8) return (
    <div className="space-y-5">
      <div className="border rounded-lg p-4 bg-zinc-50 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">{tenant?.msa_text || "—"}</div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-1" checked={!!a.msa_accepted} onChange={e=>setField("msa_accepted", e.target.checked)} data-testid="msa-accept"/>
        <span>I have read and agree to the Master Services Agreement above.</span>
      </label>
      <details className="text-sm">
        <summary className="cursor-pointer text-zinc-700">Read the Code of Conduct</summary>
        <div className="border rounded-lg p-4 bg-zinc-50 mt-2 whitespace-pre-wrap max-h-48 overflow-y-auto">{tenant?.code_of_conduct_text || "—"}</div>
      </details>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-1" checked={!!a.code_of_conduct_accepted} onChange={e=>setField("code_of_conduct_accepted", e.target.checked)} data-testid="coc-accept"/>
        <span>I have read and agree to the Vendor Code of Conduct.</span>
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-1" checked={!!a.dpdp_consent} onChange={e=>setField("dpdp_consent", e.target.checked)} data-testid="dpdp-accept"/>
        <span>I consent to the company processing this application data under the DPDP Act 2023. I may revoke consent and request erasure at any time.</span>
      </label>
    </div>
  );

  return null;
}

// ────────────────────────────────────────────────────────────────────
// Tiny inputs
// ────────────────────────────────────────────────────────────────────

function Field({ label, children }) {
  return <div><Label className="text-xs text-zinc-600 mb-1 block">{label}</Label>{children}</div>;
}
function Section({ title, children }) {
  return <div><h3 className="text-sm font-semibold text-zinc-900 mb-3">{title}</h3>{children}</div>;
}
function Center({ children }) { return <div className="max-w-md mx-auto py-20 text-center text-zinc-600">{children}</div>; }

function AddressBlock({ title, v, onChange, testidPrefix }) {
  return (
    <Section title={title}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Line 1"><Input value={v.line1||""} onChange={e=>onChange("line1", e.target.value)} data-testid={`${testidPrefix}-line1`}/></Field>
        <Field label="Line 2"><Input value={v.line2||""} onChange={e=>onChange("line2", e.target.value)}/></Field>
        <Field label="City"><Input value={v.city||""} onChange={e=>onChange("city", e.target.value)}/></Field>
        <Field label="State"><Input value={v.state||""} onChange={e=>onChange("state", e.target.value)}/></Field>
        <Field label="Pincode"><Input value={v.pincode||""} onChange={e=>onChange("pincode", e.target.value)}/></Field>
        <Field label="Country"><Input value={v.country||"India"} onChange={e=>onChange("country", e.target.value)}/></Field>
      </div>
    </Section>
  );
}

function ChipMulti({ options, value, onChange }) {
  const set = new Set(value || []);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => {
        const on = set.has(o);
        return (
          <button key={o} type="button"
            onClick={() => { const n = new Set(set); on ? n.delete(o) : n.add(o); onChange([...n]); }}
            className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

function Shell({ tenant, subnav, children }) {
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          {tenant?.logo_url ? <img src={tenant.logo_url} alt="" className="h-8"/> : <div className="font-semibold text-zinc-900">SaarOne</div>}
          {tenant?.company_name && <div className="text-sm text-zinc-500">· Vendor onboarding for <strong className="text-zinc-700">{tenant.company_name}</strong></div>}
        </div>
        {subnav && <div className="max-w-5xl mx-auto px-4 sm:px-6">{subnav}</div>}
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">{children}</main>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Per-step validation + writable-subset picker
// ────────────────────────────────────────────────────────────────────

function pickWritable(app, stepIdx) {
  const a = app || {};
  const groups = [
    ["legal_name","trade_name","entity_type","year_established","website","employee_count_range"],
    ["pan","gstin","gst_state_code","tan","msme_registered","udyam_number","msme_category","iec_code","cin","fssai","industry_licenses"],
    ["primary_contact_name","primary_contact_designation","primary_contact_phone","accounts_contact_name","accounts_contact_email","accounts_contact_phone","escalation_contact_name","escalation_contact_email","escalation_contact_phone"],
    ["registered_address","operations_address","branch_addresses"],
    ["bank_name","bank_branch","ifsc","account_number","account_holder_name","account_type","upi_id"],
    ["business_categories","products_services_offered","key_customers","annual_turnover_range","locations_served","payment_terms_requested","has_credit_facility"],
    ["iso_certifications","quality_certifications","insurance_policies","diversity_certifications","has_been_blacklisted","blacklist_disclosure","litigation_disclosure"],
    ["documents"],
    [],
  ];
  const out = {};
  for (const k of groups[stepIdx] || []) if (a[k] !== undefined) out[k] = a[k];
  return out;
}

function validateStep(app, idx) {
  const a = app || {};
  if (idx === 0) {
    if (!a.legal_name) return "Legal name is required";
    if (!a.entity_type) return "Entity type is required";
  }
  if (idx === 1) {
    if (a.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(a.pan)) return "PAN format invalid";
    if (a.gstin && a.gstin.length !== 15) return "GSTIN must be 15 characters";
  }
  if (idx === 2) {
    if (!a.primary_contact_name) return "Primary contact name is required";
    if (!a.primary_contact_phone) return "Primary contact phone is required";
  }
  if (idx === 3) {
    const reg = a.registered_address || {};
    if (!reg.line1 || !reg.city || !reg.pincode) return "Registered address (line 1, city, pincode) is required";
  }
  if (idx === 4) {
    if (!a.account_holder_name || !a.account_number || !a.ifsc || !a.bank_name) return "Account holder, number, IFSC and bank are all required";
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(a.ifsc)) return "IFSC format invalid";
  }
  if (idx === 5) {
    if (!a.products_services_offered) return "Tell us about your products / services";
  }
  return null;
}

function updateArrAt(setField, app, key, idx, value) {
  const arr = [...(app?.[key] || [])];
  while (arr.length <= idx) arr.push("");
  arr[idx] = value;
  setField(key, arr.filter((v,i,arr) => v || i < arr.length-1));
}
